import { Cloudinary } from '@cloudinary/url-gen';
import { dpr, format, quality } from '@cloudinary/url-gen/actions/delivery';
import { crop, fill, limitFill, pad, thumbnail } from '@cloudinary/url-gen/actions/resize';
import { face } from '@cloudinary/url-gen/qualifiers/focusOn';
import { auto as autoFormat } from '@cloudinary/url-gen/qualifiers/format';
import { autoGravity, focusOn } from '@cloudinary/url-gen/qualifiers/gravity';
import { auto as autoQuality } from '@cloudinary/url-gen/qualifiers/quality';
import { css } from 'styled-components';
const cl = new Cloudinary({
    cloud: {
        cloudName: 'lcci',
    },
    url: {
        secure: true,
    },
});
/**
 * Create an image URL for ImageProcessor
 * @param image The image model
 * @param imageParams The image parameters
 * @returns The image URL
 */
export function imageUrl(image, imageParams) {
    if (!image?.src) {
        return '';
    }
    if (image.src.startsWith('http')) {
        return image.src;
    }
    let clImage = cl.image(image.src);
    const resizeAction = getResizeAction(image, imageParams);
    if (resizeAction) {
        clImage = clImage.resize(resizeAction);
    }
    clImage = clImage.delivery(getFormatAction(imageParams)).delivery(quality(autoQuality()));
    if (imageParams.dpr) {
        clImage = clImage.delivery(dpr(imageParams.dpr));
    }
    // Set version to enable mass invalidation.
    // clImage.setVersion(process.env.CLOUDINARY_VERSION ?? '1');
    return clImage.toURL();
}
function getResizeAction(image, imageParams) {
    if (!imageParams.width && !imageParams.height) {
        return undefined;
    }
    let resizeAction;
    switch (imageParams.crop) {
        case 'crop':
            resizeAction = crop();
            break;
        case 'fill':
            resizeAction = fill();
            break;
        case 'lfill':
            resizeAction = limitFill();
            break;
        case 'pad':
            resizeAction = pad();
            break;
        case 'thumb':
            resizeAction = thumbnail();
            break;
        default:
            resizeAction = fill();
            break;
    }
    if (imageParams.width) {
        resizeAction = resizeAction.width(imageParams.width);
    }
    else if (imageParams.widthratio && imageParams.crop !== 'pad') {
        resizeAction = resizeAction.aspectRatio(imageParams.widthratio);
    }
    if (imageParams.height) {
        resizeAction = resizeAction.height(imageParams.height);
    }
    else if (imageParams.heightratio && imageParams.crop !== 'pad') {
        resizeAction = resizeAction.aspectRatio(imageParams.heightratio);
    }
    if (imageParams.gravity === 'face') {
        resizeAction = resizeAction.gravity(focusOn(face()));
    }
    else if (imageParams.crop !== 'pad') {
        resizeAction = resizeAction.gravity(autoGravity());
    }
    // Zoom can only be applied when using crop or thumb mode.
    if ((imageParams.crop === 'crop' || imageParams.crop === 'thumb') && imageParams.zoom) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        resizeAction = resizeAction.zoom(imageParams.zoom);
    }
    return resizeAction;
}
function getFormatAction(imageParams) {
    let formatAction;
    if (imageParams.format) {
        formatAction = format(imageParams.format);
    }
    else {
        formatAction = format(autoFormat());
    }
    return formatAction;
}
export function getSrcs(image, upper, lower, steps, ratio, otherParams = {}) {
    const sizes = getRange(upper, lower, steps);
    return sizes.map((size) => `${imageUrl(image, { width: size, heightratio: ratio, ...otherParams })} ${size}w`);
}
export function getRetinaSrcs(image, width, ratio, otherParams = {}) {
    const dprs = [1, 1.5, 2, 2.5, 3];
    return dprs.map((dpr) => `${imageUrl(image, { width: width, heightratio: ratio, dpr, ...otherParams })} ${dpr}x`);
}
function getRange(upper, lower, steps) {
    const difference = upper - lower;
    const increment = difference / (steps - 1);
    return [
        lower,
        ...Array(steps - 2)
            .fill('')
            .map((_, index) => Math.ceil(lower + increment * (index + 1))),
        upper,
    ];
}
/**
 * Create an pdf URL
 * @param url The pdf url
 * @returns The pdf URL
 */
export function pdfUrl(url) {
    if (!url) {
        return '';
    }
    const clImage = cl.image(url);
    return clImage.toURL();
}
/**
 * Re-usable styles for image container where the
 * image needs to maintain it's aspect ratio
 *
 * @param width
 * @param height
 * @returns
 */
export function imageWrapperPreserveRatio(width, height) {
    if (!width && !height) {
        return null;
    }
    return css `
    position: relative;
    overflow: hidden;
    overflow: clip;
    width: 100%;
    aspect-ratio: ${width / height};
  `;
}
/**
 * Calculates the percentage bottom padding
 * required to maintain a certain ratio.
 *
 * To be used in cases where the image ratio
 * changes based on viewport.
 *
 * @param width
 * @param height
 * @returns padding-bottom: {ratioPercentage}%
 */
export function imageWrapperRatioPadding(width, height) {
    if (!width && !height) {
        return '';
    }
    const ratioPercentage = ((height / width) * 100).toFixed(2);
    return `padding-bottom: ${ratioPercentage}%`;
}
/**
 * Re-usable styles for an image placed within
 * a container maintaining aspect ratio
 */
export const imageCoverContainer = (relative) => css `
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  ${!relative && 'position: absolute; top: 0;left: 0;'}
`;
